import React from "react";
import { Dialog } from "primereact/dialog";
import DentalSelctedTooth from "../shared/DentalSelctedTooth";
import { handlePrintData } from "../utils/PrintData";
import { extractTextFromHTML } from "../utils/ExtractTextFromHtml";
import { formatDuration } from "../utils/FormateDuration";
import moment from "moment";

function DCAFData({ item, edit }) {
  const headerOne = [
    { id: 1, name: "Code" },
    { id: 2, name: "Dental Service" },
    { id: 3, name: "Tooth No." },
    { id: 4, name: "Cost" },
  ];

  const headerTwo = [
    { id: 1, name: "Medication Name (Generic Name)" },
    { id: 2, name: "Type" },
    { id: 3, name: "Quantity" },
  ];
  const missingTooths = item?.supportingInfos?.filter((info) => ["missingtooth", "missing-tooth"].includes(info.category));

  const calculateTotalNet = () => {
    return item?.items?.reduce(
      (accumulator, currentItem) => accumulator + currentItem.net,
      0
    );
  };

  // Compact data display component
  const CompactDataRow = ({ label, value, className = "" }) => (
    <div className={`flex items-center gap-1 ${className}`}>
      <span className="text-xs font-semibold whitespace-nowrap">{label}:</span>
      <span className="text-xs border-b border-dotted border-black flex-1 min-w-[50px] truncate">
        {value || ""}
      </span>
    </div>
  );

  return (
    <div
      className="print-container"
      style={{ fontSize: "12px", lineHeight: "1.2" }}
    >
      <h3 className="text-lg text-center font-bold mb-2">DCAF 2.0</h3>

      {/* First Row - Compact Grid */}
      <div className="grid grid-cols-2 gap-1 mb-2">
        {/* Reception/Nurse Section */}
        <div className="border border-black p-1 rounded">
          <h3 className="text-xs font-bold mb-1">
            To be completed & ID verified by reception/nurse:
          </h3>
          <div className="space-y-1">
            <CompactDataRow
              label="Provider Name"
              value={item?.patient?.selected_plan?.payer_name || item?.patient?.insurance_plans?.[0]?.payer_name}
            />
            <CompactDataRow
              label="Insurance Company"
              value={item?.patient?.selected_plan?.payer_name || item?.patient?.insurance_plans?.[0]?.payer_name}
            />
            <CompactDataRow
              label="TPA Company"
              value={item?.patient?.selected_plan?.tpa_license_id || item?.patient?.insurance_plans?.[0]?.tpa_license_id}
            />
            <CompactDataRow
              label="Patient MRN"
              value={item?.patient?.file_no}
            />
            <CompactDataRow
              label="Document ID"
              value={item?.patient?.document_id}
            />
            <div className="flex items-center gap-1">
              <span className="text-xs font-semibold">
                Single () Married ():
              </span>
              <span className="text-xs">Plan Type</span>
              <span className="text-xs border-b border-dotted border-black flex-1">
                {item?.patient?.selected_plan?.coverage_type || item?.patient?.insurance_plans?.[0]?.coverage_type}
              </span>
            </div>
            <CompactDataRow label="Date Of Visit" value={moment(item?.visit_date).format("DD-MM-YYYY")} />
            <div className="flex gap-2 text-xs">
              <span>New Visit()</span>
              <span>FollowUp()</span>
              <span>Refell()</span>
              <span>WalkIn()</span>
            </div>
            <div className="flex gap-2">
              <CompactDataRow
                label="Referrel"
                value={item && item.is_referral ? "YES" : "NO"}
                className="flex-1"
              />
              <CompactDataRow
                label="Refer To"
                value={item?.dischargeDisposition}
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* Patient Info Section */}
        <div className="border border-black p-1 rounded">
          <h3 className="text-xs font-bold mb-1">
            Print & fill in clear letters or Emboss Card:
          </h3>
          <div className="space-y-1">
            <CompactDataRow
              label="Insured Name"
              value={item?.patient?.full_name}
            />
            <CompactDataRow
              label="ID Card Number"
              value={item?.patient?.selected_plan?.member_card_id || item?.patient?.insurance_plans?.[0]?.member_card_id}
            />
            <div className="flex gap-1">
              <CompactDataRow
                label="Sex"
                value={item?.patient?.gender}
                className="flex-1"
              />
              <CompactDataRow
                label="Date of Birth"
                value={item?.patient?.dob}
                className="flex-1"
              />
            </div>
            <div className="flex gap-1">
              <CompactDataRow
                label="Policy Holder"
                value="YES"
                className="flex-1"
              />
              <CompactDataRow
                label="Policy Number"
                value={item?.patient?.selected_plan?.policy_number || item?.patient?.insurance_plans?.[0]?.policy_number}
                className="flex-1"
              />
            </div>
            <div className="flex gap-1">
              <CompactDataRow
                label="Date Of Expiry"
                value={item?.patient?.selected_plan?.expiry_date || item?.patient?.insurance_plans?.[0]?.expiry_date}
                className="flex-1"
              />
              <CompactDataRow
                label="Class"
                value={item?.patient?.selected_plan?.class_name || item?.patient?.insurance_plans?.[0]?.class_name}
                className="flex-1"
              />
            </div>
            <div className="flex gap-1">
              <CompactDataRow
                label="Approval"
                value={
                  item?.items?.find(
                    approvalItem => approvalItem?.approval_number
                  )?.approval_number ||
                  item?.approval_number ||
                  item?.pre_auth_ref_no
                }
                className="flex-1"
              />
              <CompactDataRow
                label="Response Date"
                value={
                  !(item?.status === "approved")
                    ? item?.response_date?.length > 16
                      ? item.response_date.substring(0, 16).replace("T", " ")
                      : item?.response_date
                    : null
                }
                className="flex-1"
              />
            </div>
            <div className="flex gap-1">
              <CompactDataRow
                label="Approval Status"
                value={item?.status}
                className="flex-1"
              />
              <CompactDataRow
                label="Approval Date"
                value={
                  item?.status === "approved"
                    ? item?.response_date?.length > 16
                      ? item.response_date.substring(0, 16).replace("T", " ")
                      : item?.response_date
                    : null
                }
                className="flex-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dentist Section */}
      <div className="border border-black p-1 rounded mb-1">
        <div className="grid grid-cols-7 gap-1">
          <div className="col-span-4">
            <h3 className="text-xs font-bold mb-1">
              To be completed by Dentist
            </h3>

            <CompactDataRow
              label="Duration of Illness"
              value={formatDuration(
                item?.vitalSigns?.find(
                  (info) => info.category === "duration-of-illness"
                )?.value
              )}
              className="mb-1"
            />

            <div className="mb-1">
              <CompactDataRow
                label="Cheif Complaints and Main Symptoms"
                value={
                  edit
                    ? extractTextFromHTML(
                      item?.medical_assessments?.chief_complaints?.value
                    )
                    : extractTextFromHTML(item?.chiefComplaints?.value)
                }
              />
            </div>

            <div className="space-y-1 mb-1">
              <CompactDataRow
                label="Significiant Signs"
                value={
                  edit
                    ? extractTextFromHTML(
                      item?.medical_assessments?.significient_signs
                    )
                    : extractTextFromHTML(item?.significantSign)
                }
              />
              <CompactDataRow
                label="Other Conditions"
                value={
                  edit
                    ? extractTextFromHTML(
                      item?.medical_assessments?.other_conditions
                    )
                    : extractTextFromHTML(item?.otherCondition)
                }
              />
              <CompactDataRow
                label="Diagnosis"
                value={
                  edit
                    ? item?.diagnosisArr
                      ?.map(
                        (diag) => `${diag.codeId} - ${diag.codeDescription}`
                      )
                      .join(", ")
                    : item?.diagnosisArray
                      ?.map(
                        (diag) => `${diag.codeId} - ${diag.codeDescription}`
                      )
                      ?.join(", ")
                }
              />
              <CompactDataRow
                label="Principal Code"
                value={
                  edit
                    ? item?.diagnosisArr?.map((diag) => diag.codeId)?.join(", ")
                    : item?.diagnosisArray
                      ?.map((diag) => diag.codeId)
                      ?.join(", ")
                }
              />
            </div>

            <hr className="border-black my-1" />

            <h3 className="text-xs font-bold mb-1">
              Please tick () Where appropriate
            </h3>
            <div className="text-xs space-y-1">
              <div className="flex gap-2">
                <span>Regular Dental Treatment ()</span>
                <span>Dental Cleaning ()</span>
              </div>
              <div className="flex gap-2 items-center">
                <span>Trauma Treatment Specify: RTA ()</span>
                <span>Work Related ()</span>
                <div className="flex items-center gap-1">
                  <span>Other</span>
                  <span className="border-b border-dotted border-black w-12"></span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-3 flex justify-center items-center">
            <div className="scale-75">
              <DentalSelctedTooth item={item} missingTooths={missingTooths} />
            </div>
          </div>
        </div>

        {/* Procedures Table */}
        <div className="mt-1">
          <p className="text-xs mb-1">
            Specify the recommended procedures using the tooth number as shown
            on the teeth map above:
          </p>

          <table className="w-full border border-black text-xs">
            <thead>
              <tr className="border border-black">
                {headerOne.map((item) => (
                  <th
                    key={item.id}
                    className="border border-black p-1 text-center font-bold"
                  >
                    {item.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {item?.items?.map((item, index) => (
                <tr key={index} className="border border-black">
                  <td className="border border-black p-1 text-center">
                    {item?.standard_code}
                  </td>
                  <td className="border border-black p-1 text-center">
                    <div>{item?.standard_description}</div>
                    {item?.reject_reason_display && (
                      <div className="text-red-500 text-xs mt-1">
                        <span className="font-semibold">Reject Reason: </span>
                        {item?.reject_reason_display}
                      </div>
                    )}
                  </td>
                  <td className="border border-black p-1 text-center">
                    {item?.body_site}
                  </td>
                  <td className="border border-black p-1 text-center">
                    {item?.net}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border border-black">
                <td colSpan="2"></td>
                <td className="border border-black p-1 text-center font-bold">
                  Total
                </td>
                <td className="border border-black p-1 text-center font-bold">
                  {calculateTotalNet()}
                </td>
              </tr>
            </tfoot>
          </table>

          <p className="text-xs font-semibold mt-1">
            Providing approvals /Coding Staff must review /Code the recommended
            Service and collect cost and complete the following:
          </p>

          <div className="flex gap-1 mt-1">
            <CompactDataRow
              label="Completed / Coded By"
              value=""
              className="flex-1"
            />
            <CompactDataRow label="Signature" value="" className="flex-1" />
            <CompactDataRow label="Date" value="" className="flex-1" />
          </div>

          {/* Medications Table */}
          <table className="w-full border border-black text-xs mt-1">
            <thead>
              <tr className="border border-black">
                {headerTwo.map((item) => (
                  <th
                    key={item.id}
                    className="border border-black p-1 text-center font-bold"
                  >
                    {item.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {item?.items?.map((item, index) => (
                <tr key={index} className="border border-black">
                  <td className="border border-black p-1 text-center">
                    {`(${item?.standard_code}) - ${item?.standard_description}`}
                  </td>
                  <td className="border border-black p-1 text-center">
                    {item?.selected_category}
                  </td>
                  <td className="border border-black p-1 text-center">
                    {item?.quantity}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs">
              In Case management form (CMF 1.0) included
            </span>
            <b className="text-xs">Yes () No()</b>
          </div>

          <div className="flex items-center gap-1 mt-1">
            <span className="text-xs whitespace-nowrap">
              Please specify Possible line of management:
            </span>
            <span className="border-b border-dotted border-black flex-1"></span>
          </div>

          <div className="flex gap-1 mt-1">
            <CompactDataRow
              label="Estimated length of Stay"
              value=""
              className="flex-1"
            />
            <CompactDataRow
              label="Estimated length of Stay"
              value=""
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Certifications */}
      <div className="border border-black p-1 rounded mb-1">
        <div className="flex gap-1">
          <div className="flex-1 border-r border-gray-300 pr-1">
            <p className="text-xs font-semibold">
              I hereby certify that ALL information mentioned are correct and
              that the medical services shown on this form were medically
              indicated and necessary.
            </p>
            <div className="flex justify-between mt-1 text-xs">
              <div className="flex flex-col">
                <span className="font-bold">Dentist</span>
                <span className="border-b border-dotted border-black w-20">
                  {edit
                    ? item?.careTeamsArr
                      ?.map(
                        (care) => care.practitionerName || care?.practitioner
                      )
                      ?.join(", ")
                    : item?.careTeamsArray
                      ?.map(
                        (care) => care.practitionerName || care?.practitioner
                      )
                      ?.join(", ")}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold">Signature</span>
                <span className="border-b border-dotted border-black w-16"></span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold">Stamp</span>
                <input type="radio" className="scale-75" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold">Date</span>
                <span className="border-b border-dotted border-black w-16"></span>
              </div>
            </div>
          </div>

          <div className="flex-1 pl-1">
            <p className="text-xs font-semibold">
              I hereby certify that ALL statements and information provided
              concerning patient identification and the present illness or
              injury are TRUE.
            </p>
            <div className="mt-1 text-xs">
              <CompactDataRow
                label="Name (and relationship (if guardian))"
                value=""
              />
              <div className="flex gap-1 mt-1">
                <CompactDataRow label="Signature" value="" className="flex-1" />
                <CompactDataRow label="Date" value="" className="flex-1" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-black p-1 rounded">
        <div className="text-xs">
          <div className="flex gap-1">
            <span className="font-semibold w-1/4">
              For Insurance Company Use Only:
            </span>
            <div className="flex w-3/4 justify-between">
              <span>Approved ( )</span>
              <span>Not Approved ( )</span>
              <span>Approval No:</span>
              <span>Approval validity:</span>
            </div>
          </div>
          <div className="mt-1">
            Days Comments (include approved days/services if different from the
            requested):
          </div>
        </div>

        <hr className="border-black my-1" />

        <div className="flex gap-1 text-xs">
          <CompactDataRow
            label="Approved/Disapproved By"
            value=""
            className="flex-1"
          />
          <CompactDataRow label="Signature" value="" className="flex-1" />
          <CompactDataRow label="Date" value="" className="flex-1" />
        </div>
      </div>

      <p className="text-xs font-bold mt-1">
        (*) This is applicable only in case of manual DCAF
      </p>
    </div>
  );
}

function DcafForm({
  showDCAF = true,
  setShowDCAF = () => { },
  item = null,
  edit = false,
}) {
  const handlePrint = async () => {
    const printContent = document.getElementById("print-content");
    if (!printContent) return;
    await handlePrintData(printContent.innerHTML);
  };

  return (
    <Dialog
      visible={showDCAF}
      onHide={() => setShowDCAF(false)}
      style={{ width: "95vw", maxWidth: "1200px" }}
      header="Dental Claim Authorization Form"
      modal
      breakpoints={{ "960px": "95vw", "640px": "95vw" }}
      draggable={false}
      resizable={false}
      className="max-h-screen"
    >
      <div id="print-content" className="overflow-auto max-h-[70vh]">
        <DCAFData item={item} edit={edit} />
      </div>
      <div className="sticky bottom-0 bg-white p-3 border-t">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-gray-300 bg-white py-1 px-3 text-sm hover:bg-gray-50"
            onClick={() => setShowDCAF(false)}
          >
            Close
          </button>
          <button
            type="button"
            className="rounded bg-indigo-600 py-1 px-3 text-sm text-white hover:bg-indigo-700"
            onClick={handlePrint}
          >
            Print
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export default DcafForm;

// import React from "react";

// import { Dialog } from "primereact/dialog";
// import DentalSelctedTooth from "../shared/DentalSelctedTooth";
// import { handlePrintData } from "../utils/PrintData";
// import { extractTextFromHTML } from "../utils/ExtractTextFromHtml";

// function DCAFData({ item, edit }) {
//   const headerOne = [
//     { id: 1, name: "Code" },
//     { id: 2, name: "Dental Service" },
//     { id: 3, name: "Tooth No." },
//     { id: 4, name: "Cost" },
//   ];
//   const headerthree = [
//     { id: 1, name: "Code" },
//     { id: 2, name: "Reject Reason Code" },
//     { id: 3, name: "Reject Reason Description" },
//   ];
//   const headerTwo = [
//     { id: 1, name: "Medication Name (Generic Name)" },
//     { id: 2, name: "Type" },
//     { id: 3, name: "Quantity" },
//   ];
//   const headerTwoData = [
//     { id: 1, name: "Medication Name (Generic Name)" },
//     { id: 2, name: "Type" },
//     { id: 3, name: "Quantity" },
//   ];

//   const calculateTotalNet = () => {
//     return item?.items?.reduce(
//       (accumulator, currentItem) => accumulator + currentItem.net,
//       0
//     );
//   };

//   return (
//     <div className="">
//       <h3 className="text-2xl text-center font-bold">DCAF 2.0</h3>
//       <div className="grid grid-cols-2 gap-2 my-4">
//         <div className=" border-2 border-black py-3 px-2 rounded">
//           <h3 className="text- font-black">
//             <b>To be completed &amp; ID verified by the reception/nurse:</b>
//           </h3>
//           <div className="data py-2">
//             <div className="flex flex-col gap-2">
//               <div className="one flex gap-2 items-center ">
//                 <p className="text-gray-800 font-semibold">Provider Name :</p>

//                 <p className="text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                   {item?.patient?.insurance_plans?.[0]?.payer_name}
//                 </p>
//               </div>
//               <div className="two flex gap-2 items-center ">
//                 <p className="text-gray-800 font-semibold">
//                   Insurance Company Name :
//                 </p>

//                 <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                   {item?.patient?.insurance_plans?.[0]?.payer_name}
//                 </p>
//               </div>
//               <div className="three flex gap-2 items-center ">
//                 <p className="text-gray-800 font-semibold">
//                   TPA Company Name :
//                 </p>

//                 <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                   {item?.patient?.insurance_plans?.[0]?.tpa_license_id}
//                 </p>
//               </div>
//               <div className="four flex gap-2 items-center ">
//                 <p className="text-gray-800 font-semibold">Patient File No :</p>

//                 <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                   {item?.patient?.document_id}
//                 </p>
//               </div>
//               <div className="five flex gap-2 items-center ">
//                 <p className="text-gray-800 font-semibold">
//                   Single () Married () :
//                 </p>
//                 <div className="flex gap-2">
//                   <p>Plan Type</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.patient?.insurance_plans?.[0]?.coverage_type}
//                   </p>
//                 </div>
//               </div>

//               <div className="six flex gap-2 items-center ">
//                 <p className="text-gray-800 font-semibold">Date Of Visit :</p>

//                 <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                   No
//                 </p>
//               </div>
//               <div className="seven flex gap-4 items-center ">
//                 <p className="text-gray-800 ">New Visit()</p>
//                 <p className="text-gray-800"> FollowUp() </p>
//                 <p className="text-gray-800"> Refell() </p>
//                 <p className="text-gray-800"> WalkIn() </p>
//               </div>

//               <div className="eight flex gap-2 items-center ">
//                 <div className="flex gap-3 items-center">
//                   <p className="text-gray-800 font-semibold">Referrel :</p>

//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item && item.is_referral ? "YES" : "NO"}
//                     <sup className="text-red-900 text-xl"></sup>
//                   </p>
//                 </div>
//                 <div className="flex gap-3 items-center">
//                   <p className="text-gray-800 font-semibold">Refer To:</p>

//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.dischargeDisposition}
//                     <sup className="text-red-900 text-xl"></sup>
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//         <div className=" border-2 border-black py-3 px-2 rounded-md">
//           <h3 className="text- font-black">
//             <b>Print &amp; fill in clear letters or Emboss Card:</b>
//           </h3>
//           <div className="data py-2">
//             <div className="flex flex-col gap-2">
//               <div className="one flex gap-2 items-center ">
//                 <p className="text-gray-800 font-semibold">Insured Name :</p>

//                 <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black capitalize">
//                   {item?.patient?.full_name}
//                 </p>
//               </div>

//               <div className="two flex gap-2 items-center justify-between">
//                 <div className="flex gap-2 ">
//                   <p className="text-gray-800 font-semibold">
//                     ID Card Number :
//                   </p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.patient?.insurance_plans?.[0]?.member_card_id}
//                   </p>
//                 </div>
//               </div>

//               <div className="two flex gap-2 items-center  w-full">
//                 <div className="flex gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Sex :</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black capitalize">
//                     {item?.patient?.gender}
//                   </p>
//                 </div>
//                 <div className="flex items-center gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Date of Birth :</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.patient?.dob}
//                   </p>
//                 </div>
//               </div>

//               <div className="three flex gap-2 items-center w-full">
//                 <div className="flex gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Policy Holder :</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     YES
//                   </p>
//                 </div>
//                 <div className="flex gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Policy Number :</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.patient?.insurance_plans?.[0]?.policy_number}
//                   </p>
//                 </div>
//               </div>

//               <div className="four flex gap-2 items-center w-full">
//                 <div className="flex gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">
//                     Date Of Expiry :
//                   </p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.patient?.insurance_plans?.[0]?.expiry_date}
//                   </p>
//                 </div>
//                 <div className="flex  gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Class :</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.patient?.insurance_plans?.[0]?.class_name}
//                   </p>
//                 </div>
//               </div>
//               <div className="five flex gap-2 items-center w-full">
//                 <div className="flex gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Approval :</p>

//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.pre_auth_ref_no}
//                   </p>
//                 </div>
//                 <div className="flex items-center gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Response Date :</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {!(item?.status === "approved")
//                       ? item?.response_date && item?.response_date?.length > 16
//                         ? item?.response_date.substring(0, 16).replace("T", " ")
//                         : item?.response_date
//                       : null}
//                   </p>
//                 </div>
//               </div>
//               <div className="five flex gap-2 items-center w-full ">
//                 <div className="flex gap-3 w-1/2">
//                   <p className="text-gray-800 font-semibold">
//                     Approval Status :
//                   </p>

//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {/* {data?.getPreAuthTransactionDetails.Data.} */}
//                     {item?.status}
//                   </p>
//                 </div>
//                 <div className="flex items-start  gap-2 w-1/2">
//                   <p className="text-gray-800 font-semibold">Approval Date :</p>
//                   <p className=" text-gray-800  font-mendium border-b-2 border-dotted border-black">
//                     {item?.status === "approved"
//                       ? item?.response_date && item?.response_date?.length > 16
//                         ? item?.response_date.substring(0, 16).replace("T", " ")
//                         : item?.response_date
//                       : null}
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className="second-Row border-2 border-black py-3 px-2 rounded-md ">
//         <div className="grid grid-cols-7">
//           <div className="col-span-4">
//             <h3 className="text- font-black">
//               <b>To be completed by Dentist</b>
//             </h3>
//             <div>
//               <div className="data-two py-1 flex gap-4 ">
//                 <p className="text-gray-800 font-semibold">
//                   Duration of Illness :
//                 </p>
//                 <div className="flex gap-1">
//                   <div className=" border-b-2 border-black border-dotted w-20">
//                     <p className="text-gray-800  font-semibold">
//                       {item?.vitalSigns?.find(
//                         (info) => info.category === "duration-of-illness"
//                       )?.value || ""}
//                     </p>
//                   </div>
//                   <p className="text-gray-800 font-semibold">(Days)</p>
//                 </div>
//               </div>

//               <div className="data-three py-2">
//                 <div className="flex gap-2">
//                   <p className="text-gray-800 font-semibold">
//                     Cheif Complaints and Main Symptoms :
//                   </p>

//                   <p className="flex-1 border-b-2 border-black border-dotted">
//                     {edit
//                       ? extractTextFromHTML(
//                           item?.medical_assessments?.chief_complaints?.value
//                         )
//                       : extractTextFromHTML(item?.chiefComplaints?.value)}
//                   </p>
//                 </div>
//               </div>
//               <div className="flex flex-col gap-4 mt-5">
//                 <div className="flex gap-4  ">
//                   <p className="text-gray-800 font-semibold ">
//                     Significiant Signs :
//                   </p>
//                   <div className="flex-1 border-b-2 border-black border-dotted">
//                     <p className="text-gray-800  font-semibold ">
//                       {edit
//                         ? extractTextFromHTML(
//                             item?.medical_assessments?.significient_signs
//                           )
//                         : extractTextFromHTML(item?.significantSign)}
//                     </p>
//                   </div>
//                 </div>
//                 <div className="flex gap-4  ">
//                   <p className="text-gray-800 font-semibold ">
//                     {" "}
//                     Other Conditions :
//                   </p>
//                   <div className="flex-1 border-b-2 border-black border-dotted">
//                     <p className="text-gray-800  font-semibold ">
//                       {edit
//                         ? extractTextFromHTML(
//                             item?.medical_assessments?.other_conditions
//                           )
//                         : extractTextFromHTML(item?.otherCondition)}{" "}
//                     </p>
//                   </div>
//                 </div>
//                 <div className="flex gap-4  ">
//                   <p className="text-gray-800 font-semibold "> Diagnosis :</p>
//                   <div className="flex-1 border-b-2 border-black border-dotted">
//                     <p className="text-gray-800  font-semibold ">
//                       {edit
//                         ? item?.diagnosisArr
//                             ?.map((diag) => {
//                               return `${diag.codeId} - ${diag.codeDescription}`;
//                             })
//                             .join(", ")
//                         : item?.diagnosisArray
//                             ?.map((diag) => {
//                               return `${diag.codeId} - ${diag.codeDescription}`;
//                             })
//                             ?.join(", ")}
//                     </p>
//                   </div>
//                 </div>
//                 <div className="flex items-center gap-3">
//                   <div className="flex gap-4  flex-1">
//                     <p className="text-gray-800 font-semibold ">
//                       {" "}
//                       Principal Code :
//                     </p>
//                     <div className="flex-1 border-b-2 border-black border-dotted">
//                       <p className="text-gray-800  font-semibold ">
//                         {edit
//                           ? item?.diagnosisArr
//                               ?.map((diag) => {
//                                 return `${diag.codeId} `;
//                               })
//                               ?.join(", ")
//                           : item?.diagnosisArray
//                               ?.map((diag) => {
//                                 return `${diag.codeId}`;
//                               })
//                               ?.join(", ")}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//             <div className="border-2 my-3 border-black ">
//               <hr className="border-b-2 boder-black" />
//             </div>

//             <div className="third-data">
//               <h3 className="text- font-black">
//                 <b> Please tick () Where appropriate</b>
//               </h3>

//               <div className="flex flex-col py-2 gap-2">
//                 <div className="one flex gap-3 items-center">
//                   <p className="text-gray-800 font-semibold">
//                     Regular Dental Treatment ()
//                   </p>
//                   <p className="text-gray-800 font-semibold">
//                     Dental Cleaning ()
//                   </p>
//                 </div>
//                 <div className="two flex justify-between items-center">
//                   <div className="one flex gap-3 items-center">
//                     <p className="text-gray-800 font-semibold">
//                       Trauma Treatment Specify: RTA ()
//                     </p>
//                     <p className="text-gray-800 font-semibold">
//                       Work Related ()
//                     </p>
//                     <div className="data-two py-1 flex gap-2 ">
//                       <p className="text-gray-800 font-semibold">Other</p>
//                       <div className="flex gap-1">
//                         <div className=" border-b-2 border-black border-dotted w-20">
//                           <p className="text-gray-800  font-semibold"> </p>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className=" flex justify-center items-center col-span-3">
//             <DentalSelctedTooth item={item} />
//           </div>
//         </div>
//         <div className="flex flex-col py-3 gap-3">
//           <p>
//             Specify the recommended procedures using the tooth number as shown
//             on the teeth map above:
//           </p>
//           <table className="border- border-black table-hover">
//             <thead className="border-2 border-black">
//               <tr>
//                 {headerOne.map((item, index) => {
//                   return (
//                     <th className="py text-center" key={index}>
//                       {item.name}
//                     </th>
//                   );
//                 })}
//               </tr>
//             </thead>
//             <tbody className="border-2 border-black">
//               {item?.items?.map((item, index) => {
//                 {
//                   /* item.type === "oral-health-op" ? */
//                 }
//                 return (
//                   <tr>
//                     <td
//                       className="text-center py-2
//           border-2 border-black"
//                     >
//                       {item?.standard_code}
//                     </td>

//                     <td
//                       className="text-center py-2
//                       border-2 border-black "
//                     >
//                       <div>{item?.standard_description}</div>
//                       {item?.reject_reason_display ? (
//                         <div className="text-sm text-red-500 mt-1">
//                           <span className="font-semibold">Reject Reason: </span>
//                           {item?.reject_reason_display}
//                         </div>
//                       ) : null}
//                     </td>
//                     <td
//                       className="text-center py-2
//                       border-2 border-black "
//                     >
//                       {item?.body_site}
//                     </td>

//                     <td
//                       className="text-center py-2
//                       border-2 border-black"
//                     >
//                       {item?.net}
//                     </td>
//                     {/* <td
//                       className="text-center py-2
//           border-1 border-r-2 border-r-black"
//                     >
//                       {item?.approved_value}
//                     </td> */}
//                     {/* <td
//                       className="text-center py-2
//           border-1 border-r-2 border-r-black"
//                     >
//                       {item?.status}
//                     </td> */}
//                   </tr>
//                 );
//               })}
//             </tbody>
//             <tr>
//               <td></td>
//               <td></td>
//               <td
//                 className="text-center py-2
//           border-2  border-black font-bold"
//               >
//                 Total
//               </td>
//               <td
//                 className="text-center py-2
//           border-2  border-black font-bold"
//               >
//                 {calculateTotalNet()}
//               </td>
//             </tr>
//           </table>

//           <p className="text-gray-800 font-semibold">
//             Providing approvals /Coding Stff must review /Code the recommended
//             Service and collect cose and complete the follwing :
//           </p>
//           <div className="flex items-center gap-3">
//             <div className="flex gap-4  flex-1">
//               <p className="text-gray-800 font-semibold ">
//                 {" "}
//                 Completed / Coded By :
//               </p>
//               <div className="flex-1 border-b-2 border-black border-dotted">
//                 <p className="text-gray-800  font-semibold "> </p>
//               </div>
//             </div>
//             <div className="flex gap-4  flex-1">
//               <p className="text-gray-800 font-semibold "> Signature :</p>
//               <div className="flex-1 border-b-2 border-black border-dotted">
//                 <p className="text-gray-800  font-semibold "> </p>
//               </div>
//             </div>
//             <div className="flex gap-4  flex-1">
//               <p className="text-gray-800 font-semibold "> Date :</p>
//               <div className="flex-1 border-b-2 border-black border-dotted">
//                 <p className="text-gray-800  font-semibold "> </p>
//               </div>
//             </div>
//           </div>

//           <table className="border- border-black table-hover">
//             <thead className="border-2 border-black">
//               <tr>
//                 {headerTwo.map((item, index) => {
//                   return (
//                     <th className="py text-center" key={index}>
//                       {item.name}
//                     </th>
//                   );
//                 })}
//               </tr>
//             </thead>
//             <tbody className="border-2 border-black">
//               {item?.items?.map((item, index) => {
//                 return (
//                   <tr>
//                     <td
//                       className="text-center py-2
//           border-1 border-r-2 border-r-black"
//                     >
//                       {`(${item?.standard_code}) - ${item?.standard_description}`}
//                     </td>

//                     <td
//                       className="text-center py-2
//           border-1 border-r-2 border-r-black"
//                     >
//                       {item?.selected_category}
//                     </td>
//                     <td
//                       className="text-center py-2
//           border-1 border-r-2 border-r-black"
//                     >
//                       {item?.quantity}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//           <div className="flex gap-5 items-center">
//             <p className="text-gray-700">
//               In Case management form (CMF 1.0) included
//             </p>
//             <b className="text-black">Yes () No()</b>
//           </div>
//           <div className="flex gap-5 items-center">
//             <div className="">
//               <p className="text-gray-700">
//                 Please specify Possible line of management when applicable :
//               </p>
//             </div>
//             <div className="flex-1 border-b-2 border-black border-dotted">
//               <p className="text-gray-800"></p>
//             </div>
//           </div>
//           <div className="flex gap-5 items-center">
//             <div className="flex-1 flex items-center gap-3">
//               <div className="">
//                 <p className="text-gray-800 font-black">
//                   Estimated length of Stay :
//                 </p>
//               </div>
//               <div className="flex-1 border-b-2 border-black border-dotted">
//                 <p className="text-gray-800"></p>
//               </div>
//             </div>
//             <div className="flex-1 flex items-center gap-3">
//               <div className="">
//                 <p className="text-gray-800 font-black">
//                   Estimated length of Stay :
//                 </p>
//               </div>
//               <div className="flex-1 border-b-2 border-black border-dotted">
//                 <p className="text-gray-800"></p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       {/* THIRD ROW */}
//       <div className="second-Row border-2 border-black py-3 px-2 rounded-md mt-2">
//         <div className="flex gap-5">
//           <div
//             className="flex-1 border-r-2 border-gray
//   p-4"
//           >
//             <p className="text-gray-700 font-semibold">
//               I hereby certify that ALL information mentioned are correct and
//               that the medical services shown on this form were medically
//               indicated and necessary for the management of this case.
//             </p>
//             <div className="flex mt-3 justify-between">
//               <div className="flex-col gap-4 flex ">
//                 <p className="text-gray-700 font-black">Dentist</p>
//                 <div className="border-b-2 flex-1 border-black border-dotted">
//                   {edit
//                     ? item?.careTeamsArr
//                         ?.map((care) => {
//                           return `${
//                             care.practitionerName || care?.practitioner
//                           }`;
//                         })
//                         ?.join(", ")
//                     : item?.careTeamsArray
//                         ?.map((care) => {
//                           return `${
//                             care.practitionerName || care?.practitioner
//                           }`;
//                         })
//                         ?.join(", ")}
//                 </div>
//               </div>
//               <div className="flex-col gap-4 flex">
//                 <p className="text-gray-700 font-black">Signature</p>
//                 <div className="border-b-2 flex-1 border-black border-dotted"></div>
//               </div>
//               <div className="flex-col gap-4 flex">
//                 <p className="text-gray-700 font-black">Stamp</p>
//                 <div className=" ml-4 ">
//                   <input type="radio" />
//                 </div>
//               </div>
//               <div className="flex-col gap-4 flex">
//                 <p className="text-gray-700 font-black">Date</p>
//                 <div className="border-b-2 flex-1 border-black border-dotted"></div>
//               </div>
//             </div>
//           </div>
//           <div
//             className="flex-1
//   p-4"
//           >
//             <p className="text-gray-700 font-semibold ">
//               I hereby certify that ALL statements and information provided
//               concerning patient identification and the present illness or
//               injury are TRUE.
//             </p>
//             <div className="mt-4">
//               <div className="flex gap-4">
//                 <p className="text-gray-800 font-black flex-2">
//                   Name (and relationship (if guardian)):
//                 </p>
//                 <div className="border-b-2 border-black border-dotted flex-1"></div>
//               </div>
//               <div className="flex mt-4">
//                 <div className="flex gap-4 flex-1">
//                   <p className="text-gray-800 font-black flex-2">Signature:</p>
//                   <div className="border-b-2 border-black border-dotted flex-1"></div>
//                 </div>
//                 <div className="flex gap-4 flex-1">
//                   <p className="text-gray-800 font-black flex-2">Date:</p>
//                   <div className="border-b-2 border-black border-dotted flex-1"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       {/* FOURTH ROW */}
//       <div className="second-Row border-2 border-black p-4 rounded-md mt-2">
//         <div>
//           <div className="flex gap-4">
//             <p className="text-gray-700 w-[20%] font-semibold">
//               For Insurance Company Use Only:
//             </p>
//             <div className="flex w-[80%] justify-evenly items-center">
//               <div>Approved ( )</div>
//               <div>Not Approved ( )</div>
//               <div>Approval No:</div>
//               <div> Approval validity:</div>
//               <div> Approval validity:</div>
//             </div>
//           </div>
//           <div>
//             Days Comments (include approved days/services if different from the
//             requested):
//           </div>
//         </div>
//         <hr className="border-b-4 mt-3 border-black font-bold" />
//         <div className="flex mt-4">
//           <div className="one flex gap-3 flex-1">
//             <p className="text-gray-700  font-black">
//               Approved/Disapproved By:
//             </p>
//             <div className="border-b-2 border-black border-dotted flex-1"></div>
//           </div>
//           <div className="two flex gap-3 flex-1">
//             <p className="text-gray-700  font-black">Signature:</p>
//             <div className="border-b-2 border-black border-dotted flex-1"></div>
//           </div>
//           <div className="two flex gap-3 flex-1">
//             <p className="text-gray-700  font-black">Date :</p>
//             <div className="border-b-2 border-black border-dotted flex-1"></div>
//           </div>
//         </div>
//       </div>
//       {/*LAST ROW  */}
//       <p
//         className="text-gray-900
// font-black
// py-1
// "
//       >
//         (*) This is applicable only in case of manual DCAF
//       </p>
//     </div>
//   );
// }

// function DcafForm({
//   showDCAF = true,
//   setShowDCAF = () => {},
//   item = null,
//   edit = false,
// }) {
//   const handlePrint = async () => {
//     const printContent = document.getElementById("print-content");
//     if (!printContent) return;
//     const clonedContent = printContent.cloneNode(true);
//     clonedContent.querySelectorAll("*").forEach((el) => {
//       el.style.fontSize = "10px";
//     });
//     await handlePrintData(clonedContent.innerHTML);
//   };

//   return (
//     <Dialog
//       visible={showDCAF}
//       onHide={() => setShowDCAF(false)}
//       style={{ width: "80vw" }}
//       header="Dental Claim Authorization Form"
//       modal
//       breakpoints={{ "960px": "75vw", "640px": "90vw" }}
//       draggable={false}
//       resizable={false}
//     >
//       <div id="print-content">
//         <DCAFData item={item} edit={edit} />
//       </div>
//       <div className="sticky bottom-0 p-4 pt-3">
//         <div className="flex justify-end">
//           <button
//             type="button"
//             className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
//             onClick={() => setShowDCAF(!showDCAF)}
//           >
//             Close
//           </button>
//           <button
//             type="submit"
//             className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
//             onClick={() => handlePrint()}
//           >
//             Print
//           </button>
//         </div>
//       </div>
//     </Dialog>
//   );
// }

// export default DcafForm;
